import React, { useState } from "react";
import PricingModal from "./PricingModal";
import { getPremiumFeatures } from "../utils/premiumHelpers";

export default function PremiumUpsellPopup({
  isOpen,
  onClose,
  onSelectPlan,
  variant = "homepage", // 'homepage' | 'renewal' | 'contact-locked'
}) {
  const [showPricingModal, setShowPricingModal] = useState(false);

  if (!isOpen) return null;

  const handleUpgradeClick = () => {
    setShowPricingModal(true);
  };

  const handlePlanSelect = (plan) => {
    setShowPricingModal(false);
    onSelectPlan?.(plan);
  };

  const features = getPremiumFeatures();

  // Homepage upsell
  if (variant === "homepage") {
    return (
      <>
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full shadow-2xl overflow-hidden animate-in fade-in zoom-in">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white p-6 relative">
              <button
                onClick={onClose}
                className="absolute top-3 right-3 text-white hover:bg-indigo-700 rounded-full p-2 transition"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <h2 className="text-2xl font-bold">Welcome Back! 👋</h2>
              <p className="text-indigo-100 mt-1">Unlock premium features and connect faster</p>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Features */}
              <div className="space-y-3 mb-6">
                {features.slice(0, 3).map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <span className="text-2xl">{feature.icon}</span>
                    <div>
                      <p className="font-semibold text-gray-800 dark:text-white text-sm">{feature.title}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pricing Highlight */}
              <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-3 mb-6 text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">Starting at just</p>
                <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">₹3,000</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">for 1 month</p>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 py-2 border-2 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition font-semibold"
                >
                  Not Now
                </button>
                <button
                  onClick={handleUpgradeClick}
                  className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition font-semibold"
                >
                  Upgrade Now
                </button>
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 text-center">
                One-time payment • No auto-renewal • Cancel anytime
              </p>
            </div>
          </div>
        </div>

        <PricingModal isOpen={showPricingModal} onClose={() => setShowPricingModal(false)} onSelectPlan={handlePlanSelect} />
      </>
    );
  }

  // Renewal reminder
  if (variant === "renewal") {
    return (
      <>
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-6 relative">
              <button
                onClick={onClose}
                className="absolute top-3 right-3 text-white hover:bg-red-600 rounded-full p-2 transition"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <h2 className="text-2xl font-bold">⏰ Renew Soon!</h2>
              <p className="text-orange-100 mt-1">Your premium is expiring in 10 days</p>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 dark:text-gray-300 mb-6">
                Don't lose access to premium features! Renew now to keep seeing contact details and stay connected.
              </p>

              <div className="space-y-2 mb-6">
                <p className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <span className="text-green-500">✓</span> Continuous access to premium features
                </p>
                <p className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <span className="text-green-500">✓</span> See all contact details
                </p>
                <p className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <span className="text-green-500">✓</span> Get better visibility on your listings
                </p>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 py-2 border-2 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition font-semibold"
                >
                  Remind Later
                </button>
                <button
                  onClick={handleUpgradeClick}
                  className="flex-1 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition font-semibold"
                >
                  Renew Premium
                </button>
              </div>
            </div>
          </div>
        </div>

        <PricingModal isOpen={showPricingModal} onClose={() => setShowPricingModal(false)} onSelectPlan={handlePlanSelect} />
      </>
    );
  }

  // Contact locked upsell
  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 relative">
            <button
              onClick={onClose}
              className="absolute top-3 right-3 text-white hover:bg-indigo-700 rounded-full p-2 transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h2 className="text-2xl font-bold">🔒 Contact Info Locked</h2>
            <p className="text-purple-100 mt-1">Upgrade to Premium to contact this user</p>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              This user is a premium member. Upgrade now to see their contact details and connect directly!
            </p>

            {/* Benefits */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <span className="text-green-500 font-bold">✓</span> See phone numbers instantly
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <span className="text-green-500 font-bold">✓</span> Direct WhatsApp & call buttons
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <span className="text-green-500 font-bold">✓</span> Premium badge on your profile
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-2 border-2 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition font-semibold"
              >
                Close
              </button>
              <button
                onClick={handleUpgradeClick}
                className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition font-semibold"
              >
                Go Premium
              </button>
            </div>
          </div>
        </div>
      </div>

      <PricingModal isOpen={showPricingModal} onClose={() => setShowPricingModal(false)} onSelectPlan={handlePlanSelect} />
    </>
  );
}
